import './playerTranscripts.scss';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import { AiLogoSVGIcon, CommentSVGIcon, DownloadSVGIcon, SearchSVGIcon } from '../svg';
import { PlayerTranscriptBlock } from './playerTranscriptBlock';
import { PlayerTranscriptsLayout, PlayerTranscriptsState } from './playerTranscriptsState';
import { PlayerSearchBarControlled } from '../playerShared/playerSearchBar';
import { PlayerSectionHeaderButton } from '../playerShared/playerSectionHeaderButton';
import { PlayerSectionCloseButton } from '../playerShared/playerSectionCloseButton';
import { Transcript } from '../../entities/moment';
import { PlayerSectionOverlay } from '../playerShared/playerSectionOverlay';
import { SyncStatus } from '../../entities';
import { LoadMask } from '../loader';
import { ErrorMessageBox } from '../player/components/errorMessageBox';
import { PlayerActionButton } from '../playerShared/playerActionButton';
import { PlayerComponentName } from '../player/playerSchema';
import { AiPlayerSearchBar } from '../playerShared/aiPlayerSearchBar';
import { TranscriptConfidenceLevels } from './transcriptConfidence';
import { AudioLanguageSVGIcon, ChevronDownSVGIcon, VideoSVGIcon } from '../svg';
import { PlayerViewMode } from '../player/playerViewMode';
import { MenuItems } from '../input/menuItems';
import { Tooltip } from '../overlays';
import { DeceptioScoreLevels, isDeceptiveLevel } from './playerTranscriptsState';

type PlayerTranscriptsProps = {
  model: PlayerTranscriptsState;
  layout?: PlayerTranscriptsLayout;
  className?: string;
  showHeader?: boolean;
  playerViewMode?: PlayerViewMode;
  onPlayerViewModeChange?: (mode: PlayerViewMode) => void;
  onClose?: () => void;
}

export const PlayerTranscripts = observer(({
  model,
  layout = 'vertical',
  showHeader,
  className,
  playerViewMode,
  onPlayerViewModeChange,
  onClose
}: PlayerTranscriptsProps) => {

  useEffect(() => {
    model.mounted();
  }, [model]);

  showHeader = true;

  className = classNames('user-player-transcripts user-player-section page-section', className, layout, {
    'with-header': showHeader,
  });

  const { momentsSyncStatus, playerSearchBarState } = model;

  const proxy = layout === 'vertical' ? model.verticalComponent : model.horizontalComponent;
  const isLoading = momentsSyncStatus === SyncStatus.Fetching;
  const isDisabled = model.player.isComponentDisabled(PlayerComponentName.TranscriptsSection);

  const contentElem = (
    <section
      id="player-transcripts"
      data-tutorial-id="player-transcripts"
      data-loading={isLoading}
      aria-disabled={isDisabled}
      className={className}
      ref={proxy.ref}>
      {momentsSyncStatus === SyncStatus.Fetched && 
       (playerSearchBarState.isTranscriptSearchMode || playerSearchBarState.isIdleSearchMode) &&
        <PlayerTranscriptSectionBody
          model={model} />}

      {playerSearchBarState.isAskSearchMode &&
        <PlayerTranscriptSearchModeSectionBody
          model={model} />}

      <div className="user-player-section-mask" />

      {showHeader && (
        layout === 'vertical' ?
          <PlayerTranscriptHeaderSection
            model={model}
            playerViewMode={playerViewMode}
            onPlayerViewModeChange={onPlayerViewModeChange} /> :
          <PlayerTranscriptHorizontalHeader
            model={model}
            playerViewMode={playerViewMode}
            onPlayerViewModeChange={onPlayerViewModeChange} />
      )}

      <PlayerSectionCloseButton
        model={model}
        onClose={onClose} />

      <PlayerSectionOverlay
        visible={model.showOverlay}
        label={"Transcript"}
        status={"processing"}
      />

      {momentsSyncStatus === SyncStatus.Error &&
        <ErrorMessageBox
          content={'Could not fetch transcript because of an error.'}
        />
      }

      <LoadMask
        className="page-load-mask player-page-background user-player-section-load-mask small"
        spinner="dots3"
        spinnerClassName="medium"
        visible={momentsSyncStatus === SyncStatus.Fetching} />
    </section>
  );

  return contentElem;

});

const PlayerTranscriptHeaderSection = observer(({
  model,
  playerViewMode,
  onPlayerViewModeChange
}: PlayerTranscriptsProps) => {
  const showDownloadButton = model.player.allowDownload ?? true;
  const { playerSearchBarState } = model;

  return (<header className="user-player-section-header"
    id={'user-player-transcript-header'}>
    <div className="section-row flex-space-between">
      <h3 className="section-heading">
        <span className="icon"><CommentSVGIcon /></span>
        <span className="text">Transcript</span>
      </h3>

      <div className="section-action-block">
        <PlayerSectionHeaderButton
          model={model}
          disabled={!model.activeTranscript || model.isActiveTranscriptIntoView} />
        <span className="hline-separator" />
        {showDownloadButton && <PlayerActionButton
          className="player-download-transcript-btn"
          model={model}
          icon={<DownloadSVGIcon />}
          onClick={() => model.openDownloadTranscript()}
          tooltip={'Download Transcript'} />}
      </div>

    </div>

    <div className="section-row flex-right">
      <PlayerViewModeDropdown
        mode={playerViewMode}
        onChange={onPlayerViewModeChange} />
      {model.playerSearchBarState.isTranscriptSearchMode &&
        <PlayerSearchBarControlled
          model={playerSearchBarState.searchQuery}
          showFeedback={false}
          persistentFeedback={false}
          isCliprGptEnabled={model.playerSearchBarState.cliprGptEnabled}
          placeholder={playerSearchBarState.searchQuery.isFocused ? 'Type your keywords' : playerSearchBarState.placeholder}
          resultIndex={model.searchNavIndex}
          numberOfResults={model.searchMatchesNumber}
          navigateBack={() => model.decreaseSearchNavIndex()}
          navigateFwd={() => model.increaseSearchNavIndex()}
          onClear={() => playerSearchBarState.handleClear()} />}
      {(model.playerSearchBarState.isIdleSearchMode ||
        model.playerSearchBarState.isAskSearchMode) &&
        <AiPlayerSearchBar
          state={playerSearchBarState} />
      }
    </div>
  </header >)
});

const PlayerTranscriptHorizontalHeader = observer(({
  model,
  playerViewMode,
  onPlayerViewModeChange
}: PlayerTranscriptsProps) => {
  const showDownloadButton = model.player.allowDownload ?? true;
  const { playerSearchBarState } = model;

  return (<header className="user-player-section-header"
    id={'user-player-transcript-header'}>
    <div className="section-row flex-space-between">
      <div className="player-search-row">
        <PlayerViewModeDropdown
          mode={playerViewMode}
          onChange={onPlayerViewModeChange} />
        {model.playerSearchBarState.isTranscriptSearchMode &&
          <PlayerSearchBarControlled
            model={playerSearchBarState.searchQuery}
            showFeedback={false}
            persistentFeedback={false}
            isCliprGptEnabled={model.playerSearchBarState.cliprGptEnabled}
            placeholder={playerSearchBarState.searchQuery.isFocused ? 'Type your keywords' : playerSearchBarState.placeholder}
            resultIndex={model.searchNavIndex}
            numberOfResults={model.searchMatchesNumber}
            navigateBack={() => model.decreaseSearchNavIndex()}
            navigateFwd={() => model.increaseSearchNavIndex()}
            onClear={() => playerSearchBarState.handleClear()} />
        }
        {(model.playerSearchBarState.isIdleSearchMode ||
          model.playerSearchBarState.isAskSearchMode) &&
          <AiPlayerSearchBar
            state={playerSearchBarState} />
        }
      </div>
      <div className="section-action-block">
        <PlayerSectionHeaderButton
          model={model}
          disabled={!model.activeTranscript || model.isActiveTranscriptIntoView} />
        <span className="hline-separator" />
        {showDownloadButton && <PlayerActionButton
          className="player-download-transcript-btn"
          model={model}
          icon={<DownloadSVGIcon />}
          onClick={() => model.openDownloadTranscript()}
          tooltip={'Download Transcript'} />}
      </div>
    </div>
  </header>)
});

const PlayerViewModeDropdown = ({
  mode = 'video',
  onChange
}: {
  mode?: PlayerViewMode;
  onChange?: (mode: PlayerViewMode) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const label = mode === 'audio' ? 'Mini' : 'Medium';

  if (!onChange) return null;

  return (
    <div className="player-view-mode-dropdown">
      <button
        type="button"
        className="player-view-mode-button"
        aria-label="Player view mode"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        ref={setReferenceElement}
        onClick={() => setIsOpen(!isOpen)}>
        <span className="player-view-mode-icon">
          {mode === 'audio' ? <AudioLanguageSVGIcon /> : <VideoSVGIcon />}
        </span>
        <span className="player-view-mode-label">{label}</span>
        <span className="player-view-mode-arrow">
          <ChevronDownSVGIcon />
        </span>
      </button>

      {isOpen && (
        <MenuItems
          referenceElement={referenceElement}
          value={mode}
          items={[
            { value: 'audio', label: 'Mini' },
            { value: 'video', label: 'Medium' },
          ]}
          onChange={(_evt, value) => {
            onChange(value as PlayerViewMode);
            setIsOpen(false);
          }}
          onOutsideClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

const PlayerTranscriptSearchModeSectionBody = observer(({
  model
}: PlayerTranscriptsProps) => {

  return (
    <div className="user-player-section-body search-mode"
      data-tutorial-id='user-player-search-result-scroll'
      id={'user-player-search-result-scroll'}
      onScroll={model.handleScroll}>

      <div 
        onClick={model.playerSearchBarState.handleBackToTranscriptButtonClick}
        className="back-to-transcript">Back to transcript</div>
      <div className="transcript-search-result-container">
        <div className="transcript-search-result-item">{model.playerSearchBarState.aiSearchKey}</div>
        <div className="transcript-search-result-item answer">  
          <AiLogoSVGIcon />
          {!model.playerSearchBarState.aiAnswerLoading ? model.playerSearchBarState.aiAnswer ?? 'No answer.' : 'Loading answer ...'}
        </div>
      </div>
    </div>
  )
});

const PlayerTranscriptSectionBody = observer(({
  model
}: PlayerTranscriptsProps) => {

  return (
    <div className="user-player-section-body"
      data-tutorial-id='user-player-transcript-scroll'
      id={'user-player-transcript-scroll'}
      onScroll={model.handleScroll}> {/*transcripts-body*/}

      <div className="transcripts-empty flex-center"
        aria-hidden={model.items.length > 0}>
        <div className="message">No transcripts here.</div>
      </div>

      {model.items.length > 0 &&
        <TranscriptConfidenceLegend
          model={model} />}

      <div className="transcripts-container">
        <PlayerTranscriptList
          model={model} />
      </div>
    </div>
  )
});

export const TranscriptConfidenceLegend = observer(({
  model
}: PlayerTranscriptsProps) => {
  return (
    <div
      className="transcript-confidence-legend"
      aria-label="Transcript analysis controls">
      <div className="confidence-legend-header">
        <div className="transcript-display-toggles">
          {model.isDeceptioExperienceAvailable &&
            <button
              type="button"
              role="switch"
              aria-checked={model.isTranscriptDeceptioVisible}
              className="transcript-display-toggle"
              onClick={() => model.toggleTranscriptDeceptioVisibility()}>
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
              <span className="toggle-label">
                Deceptio {model.isTranscriptDeceptioVisible ? 'On' : 'Off'}
              </span>
            </button>}
          <button
            type="button"
            role="switch"
            aria-checked={model.isTranscriptConfidenceVisible}
            className="transcript-display-toggle"
            onClick={() => model.toggleTranscriptConfidenceVisibility()}>
            <span className="toggle-track">
              <span className="toggle-thumb" />
            </span>
            <span className="toggle-label">
              Confidence {model.isTranscriptConfidenceVisible ? 'On' : 'Off'}
            </span>
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={model.areTranscriptSpeakersVisible}
            className="transcript-display-toggle"
            onClick={() => model.toggleTranscriptSpeakersVisibility()}>
            <span className="toggle-track">
              <span className="toggle-thumb" />
            </span>
            <span className="toggle-label">
              Speakers {model.areTranscriptSpeakersVisible ? 'On' : 'Off'}
            </span>
          </button>
        </div>
      </div>

      {model.isTranscriptDeceptioActive &&
        (() => {
          const { deceptioSpeakerId } = model;

          if (!deceptioSpeakerId) {
            return null;
          }

          return (
            <div className="transcript-legend-group deceptio-legend-group">
              <p className="deceptio-legend-instruction">
                Deception detection legend. Tap on the orange and red legend boxes below to be taken
                to the first occurrence in the subject&apos;s statement where deception has been detected.
              </p>
              <div className="transcript-legend-items">
                {DeceptioScoreLevels.map(({ level, label, rangeLabel }) => {
                  // Only the two deceptive bands are hit targets: jumping to where the subject was
                  // truthful is not something an investigator ever asks for (vendor guidance).
                  const navigable = isDeceptiveLevel(level);
                  const count = model.getDeceptioLevelCount(level);
                  const className = `deceptio-legend-item deceptio-${level}`;

                  if (!navigable) {
                    return (
                      <span key={level} className={className}>
                        <span className="deceptio-label">{label}</span>
                        <span className="deceptio-range">{rangeLabel}</span>
                      </span>
                    );
                  }

                  const tooltip = count > 0
                    ? `Jump to first ${label.toLocaleLowerCase()} score`
                    : `No ${label.toLocaleLowerCase()} scores`;

                  return (
                    <Tooltip
                      key={level}
                      content={tooltip}
                      referenceClassName="transcript-legend-tooltip-ref">
                      <button
                        type="button"
                        className={className}
                        disabled={count === 0}
                        onClick={() => model.jumpToDeceptioLevel(level)}>
                        <span className="deceptio-label">{label}</span>
                        <span className="deceptio-range">
                          <SearchSVGIcon />
                          {rangeLabel}
                        </span>
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
              {model.navigableAnswers.length > 0 &&
                <div className="deceptio-nav">
                  <button type="button" onClick={() => model.jumpToPrevDeceptive()}
                    title="Previous deceptive answer">
                    Prev
                  </button>
                  <button type="button" onClick={() => model.jumpToNextDeceptive()}
                    title="Next deceptive answer">
                    Next
                  </button>
                </div>}
            </div>
          );
        })()}

      {model.isTranscriptConfidenceVisible &&
        <div className="transcript-legend-group confidence-legend-group">
          <Tooltip content="Shows how confident CLIPr is in each generated output so you know what to review more closely.">
            <span className="confidence-legend-title">
              Confidence transparency
            </span>
          </Tooltip>
          <div className="transcript-legend-items">
            {TranscriptConfidenceLevels.map(({ level, label }) => {
              const count = model.getConfidenceLevelCount(level);
              const tooltip = count > 0
                ? `Jump to first ${label.toLocaleLowerCase()} item`
                : `No ${label.toLocaleLowerCase()} items`;

              return (
                <Tooltip
                  key={level}
                  content={tooltip}
                  referenceClassName="transcript-legend-tooltip-ref">
                  <button
                    type="button"
                    className={`confidence-legend-item confidence-${level}`}
                    disabled={count === 0}
                    onClick={() => model.jumpToConfidenceLevel(level)}>
                    <span className="confidence-dot" />
                    <span className="confidence-label">{label}</span>
                    {count > 0 &&
                      <span className="confidence-count">{count}</span>}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>}
    </div>
  );
});

const PlayerTranscriptList = observer(({
  model
}: PlayerTranscriptsProps) => {

  return (<ul className="transcript-list">
    {(model.items || []).map(moment => (
      <PlayerTranscripItem key={moment.id} model={model} transcript={moment} />
    ))}
  </ul>)
})

type PlayerTranscripItemProps = {
  model: PlayerTranscriptsState;
  transcript: Transcript;
}

const PlayerTranscripItem = observer(({
  model,
  transcript
}: PlayerTranscripItemProps) => {

  return <li className="transcript-item"
    data-moment={true}
    data-moment-id={transcript.id}>
    <PlayerTranscriptBlock
      pageModel={model}
      moment={transcript} />
  </li>
})
